/**
 * Folds the `m.call.*` events a room already carries into one entry per call,
 * so a call takes its place in the conversation the way a message does.
 *
 * Nothing new is written to the room: an internal call leaves an invite, an
 * answer and a hangup behind whatever else happens, which is what lets a row
 * appear for history that was paginated in long after the call ended, and on
 * a device that was not running when the phone rang.
 *
 * The decision engine below is the same one MobileCh3 applies to its own
 * SDK's call events — keep the two in step.
 */

import {CallDirection, CallOutcome} from './callRow.ts';

export const CALL_INVITE_EVENT_TYPE = 'm.call.invite';

const CALL_ANSWER_EVENT_TYPES = ['m.call.answer', 'm.call.select_answer'];
const CALL_TERMINAL_EVENT_TYPES = ['m.call.hangup', 'm.call.reject'];

/**
 * Teardowns that mean "this ring moved elsewhere", not "nobody took it" —
 * the same exclusion CallStateRepair applies before it files a terminal
 * event against a participant.
 */
const NON_MISSING_TERMINAL_REASONS = new Set(['answered_elsewhere', 'replaced']);

const AUTOMATIC_REJECT_REASONS: ReadonlySet<string> = new Set(['user_busy', 'no_user_media']);

/**
 * How long a ring may sit with no terminal event before it reads as missed.
 * A caller whose app dies mid-ring never sends the hangup, and without this
 * the row would stay "ringing" for the life of the room. The spec's own
 * invite lifetime wins when the invite carries one.
 */
export const DEFAULT_CALL_RING_TIMEOUT_MS = 60_000;

export interface CallTimelineEvent {
    getType(): string;
    getSender(): string | null | undefined;
    getTs(): number;
    getId?(): string | undefined;
    getContent(): Record<string, unknown>;
    isDecryptionFailure?(): boolean;
}

export interface CallTimelineEntry {
    readonly matrixCallId: string;
    readonly inviteEventId?: string;
    readonly caller: string;
    readonly direction: CallDirection;
    readonly outcome: CallOutcome;
    readonly startedAt: number;
    readonly answeredAt?: number;
    readonly endedAt?: number;
    readonly durationMs?: number;
}

export function isCallInviteEvent(event: Pick<CallTimelineEvent, 'getType'>): boolean {
    return event.getType() === CALL_INVITE_EVENT_TYPE;
}

/** Every `m.call.*` event, of which only the invite is ever drawn. */
export function isCallTimelineEvent(event: Pick<CallTimelineEvent, 'getType'>): boolean {
    return event.getType().startsWith('m.call.');
}

function readCallId(content: Record<string, unknown>): string | null {
    const callId = content?.call_id;
    return typeof callId === 'string' && callId ? callId : null;
}

function readLifetime(content: Record<string, unknown>): number {
    const lifetime = content?.lifetime;
    return typeof lifetime === 'number' && Number.isFinite(lifetime) && lifetime > 0
        ? lifetime
        : DEFAULT_CALL_RING_TIMEOUT_MS;
}

interface CallSession {
    matrixCallId: string;
    inviteEventId?: string;
    caller: string;
    startedAt: number;
    ringTimeoutMs: number;
    answeredAt?: number;
    endedAt?: number;
    endedBy?: string;
    endType?: string;
    endReason?: string;
}

interface UndecryptedEvent {
    readonly sender: string;
    readonly ts: number;
}

const isUndecrypted = (event: CallTimelineEvent): boolean =>
    event.getType() === 'm.room.encrypted' || event.isDecryptionFailure?.() === true;

/**
 * @param events     room timeline events, oldest first
 * @param currentUserId the Matrix id of the viewer — decides the direction
 * @param now        clock used to age out a ring that never ended
 */
export function buildCallTimelineEntries(
    events: readonly CallTimelineEvent[],
    currentUserId: string | null | undefined,
    now: number = Date.now(),
): Map<string, CallTimelineEntry> {
    const sessions = new Map<string, CallSession>();
    const undecrypted: UndecryptedEvent[] = [];

    for (const event of events) {
        if (isUndecrypted(event)) {
            undecrypted.push({sender: event.getSender() || '', ts: event.getTs()});
            continue;
        }
        const type = event.getType();
        if (!type.startsWith('m.call.')) continue;

        const content = event.getContent() || {};
        const callId = readCallId(content);
        if (!callId) continue;

        const timestamp = event.getTs();
        const sender = event.getSender() || '';

        if (type === CALL_INVITE_EVENT_TYPE) {
            // A re-sent invite for a call already seen keeps the first ring's
            // place in the conversation; the room shows one row per call.
            if (sessions.has(callId)) continue;
            sessions.set(callId, {
                matrixCallId: callId,
                inviteEventId: event.getId?.(),
                caller: sender,
                startedAt: timestamp,
                ringTimeoutMs: readLifetime(content),
            });
            continue;
        }

        const session = sessions.get(callId);
        if (!session) continue;

        if (CALL_ANSWER_EVENT_TYPES.includes(type)) {
            // The caller never answers its own invite; an `m.call.answer` from
            // the caller's own id would be a stray echo, not a pick-up.
            if (type === 'm.call.answer' && sender === session.caller) continue;
            if (session.answeredAt === undefined) session.answeredAt = timestamp;
            continue;
        }

        if (CALL_TERMINAL_EVENT_TYPES.includes(type)) {
            if (NON_MISSING_TERMINAL_REASONS.has(String(content.reason))) continue;
            if (session.endedAt === undefined) {
                session.endedAt = timestamp;
                session.endedBy = sender;
                session.endType = type;
                session.endReason = String(content.reason ?? '');
            }
        }
    }

    const entries = new Map<string, CallTimelineEntry>();
    for (const session of sessions.values()) {
        const outcome = decideOutcome(session, now, undecrypted);
        const direction: CallDirection =
            currentUserId && session.caller === currentUserId ? 'outgoing' : 'incoming';
        const durationMs =
            session.answeredAt !== undefined && session.endedAt !== undefined
                ? Math.max(0, session.endedAt - session.answeredAt)
                : undefined;

        const entry: CallTimelineEntry = {
            matrixCallId: session.matrixCallId,
            inviteEventId: session.inviteEventId,
            caller: session.caller,
            direction,
            outcome,
            startedAt: session.startedAt,
            answeredAt: session.answeredAt,
            endedAt: session.endedAt,
            durationMs,
        };

        entries.set(session.matrixCallId, entry);
        if (session.inviteEventId) entries.set(session.inviteEventId, entry);
    }

    return entries;
}

function decideOutcome(session: CallSession, now: number, undecrypted: readonly UndecryptedEvent[]): CallOutcome {
    if (session.answeredAt !== undefined) return 'answered';
    if (session.endedAt !== undefined && isCalleeDecline(session)) return 'declined';
    if (session.endedAt === undefined && now - session.startedAt <= session.ringTimeoutMs) return 'ringing';
    return hasUndecryptedReply(session, undecrypted) ? 'unknown' : 'missed';
}

function isCalleeDecline(session: CallSession): boolean {
    return session.endType === 'm.call.reject' && session.endedBy !== session.caller && !AUTOMATIC_REJECT_REASONS.has(session.endReason ?? '');
}

function hasUndecryptedReply(session: CallSession, undecrypted: readonly UndecryptedEvent[]): boolean {
    const until = session.endedAt ?? session.startedAt + session.ringTimeoutMs;
    return undecrypted.some(event => event.sender !== session.caller && event.ts >= session.startedAt && event.ts <= until);
}
